import { useState, useRef } from 'react'
import { FlexGrid, Select, Typography, Card, Button, Spacer, Feedback } from '@telus-uds/components-web'
import { BlockRenderer } from '@/siteBuilder/renderer/contentful/BlockRenderer'
import { RichTextProps } from '../../core/RichText/RichText'
import { ImageProps } from '../../core/Image/Image'
import {
  CardContainer,
  HomeImage,
  RadioCard,
  RadioCardContainer,
  HomeSelectionsTextContainer,
  HomeSelectionsMobileContainer,
  Step1CtaContainer,
} from './Styles'
import alloyEvents from '@/siteBuilder/utils/alloyAnalytics/savingsCalculator'

export type HomeCardProps = {
  homeHeader: RichTextProps
  homeImage: ImageProps
  provinceDropdown: {
    label: string
    options: { value: string; label: string }[]
    hint: string
    customErrorMessage: string
  }
  homeLabel: string
  homeError: string
  energyLabel: string
  energyError: string
  buttonText: string
  homeValues: {
    [province: string]: {
      [energyType: string]: {
        [residenceType: string]: number
      }
    }
  }
  setCardView: (val: string) => void
  setHomeSavings: (val: number) => void
  province: string
  setProvince: (val: string) => void
  isMobile: boolean
}

const HomeCard = ({
  homeHeader,
  homeImage,
  provinceDropdown,
  homeLabel,
  homeError,
  energyLabel,
  energyError,
  buttonText,
  homeValues,
  setCardView,
  setHomeSavings,
  province,
  setProvince,
  isMobile,
}: HomeCardProps) => {
  const [homeType, setHomeType] = useState<string>('')
  const [energyType, setEnergyType] = useState<string>('')
  const [homeProvince, setHomeProvince] = useState<string>('')
  const [submitted, setSubmitted] = useState<boolean>(false)
  const homeCardRef = useRef<HTMLDivElement>(null)
  const [isAnalyticsTriggered, setIsAnalyticsTriggered] = useState(false)

  const setAnalyticsStart = () => {
    if (!isAnalyticsTriggered) {
      setIsAnalyticsTriggered(true)
      alloyEvents.toolStart(window.location.href)
    }
  }

  const getHomeTypes = (province: string) => {
    const homeData = Object.values(homeValues[province])
    const homeTypes: string[] = []
    homeData.forEach((home) => {
      Object.keys(home).forEach((key) => {
        if (!homeTypes.includes(key)) {
          homeTypes.push(key)
        }
      })
    })
    return homeTypes
  }

  const getEnergyTypes = (province: string) => {
    return Object.keys(homeValues[province])
  }

  const renderOptions = (
    label: string,
    options: string[],
    setOption: (val: string) => void,
    condition: string,
    errorMessage: string
  ) => {
    const optionClick = (option: string) => {
      setOption(option)
      setAnalyticsStart()
    }
    return (
      <>
        <Spacer space={5} />
        <Typography block variant={{ bold: true }} heading="h4">
          {label}
        </Typography>
        <Spacer space={3} />
        <FlexGrid outsideGutter={false}>
          <FlexGrid.Row>
            <RadioCardContainer>
              {options.map((option) => {
                const selected = homeType === option || energyType === option
                const card = (
                  <Card
                    tokens={{
                      contentFlexGrow: 1,
                    }}
                    variant={{ background: selected ? 'feature' : 'light' }}
                  >
                    <HomeSelectionsTextContainer>
                      <Typography>{option}</Typography>
                    </HomeSelectionsTextContainer>
                  </Card>
                )
                return (
                  <FlexGrid.Col xs={12} md={4} key={option}>
                    <RadioCard
                      role="radio"
                      onClick={() => optionClick(option)}
                      onKeyDown={() => optionClick(option)}
                      tabIndex={0}
                      aria-checked={selected}
                    >
                      {isMobile ? <HomeSelectionsMobileContainer>{card}</HomeSelectionsMobileContainer> : card}
                    </RadioCard>
                  </FlexGrid.Col>
                )
              })}
            </RadioCardContainer>
          </FlexGrid.Row>
        </FlexGrid>
        {!condition && submitted && (
          <>
            <Spacer space={1} />
            <Feedback validation="error">{errorMessage}</Feedback>
          </>
        )}
      </>
    )
  }

  const calculateSavings = () => {
    const setDeviceCard = () => {
      setHomeSavings(homeValues[province][energyType][homeType])
      setCardView('device')
      homeCardRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
    setSubmitted(true)
    homeProvince && energyType && homeType && setDeviceCard()
  }

  const renderCTAButton = () => {
    return (
      <Button
        variant={{ priority: 'high', width: 'responsive' }}
        onPress={() => {
          calculateSavings()
          alloyEvents.toolStep(homeProvince, homeType, energyType, window.location.href)
        }}
      >
        {buttonText}
      </Button>
    )
  }

  const renderHomeCardSelections = () => {
    return (
      <>
        <BlockRenderer block={homeHeader} />
        <Spacer space={6} />
        <FlexGrid gutter={false}>
          <FlexGrid.Row>
            <FlexGrid.Col sx={12} md={8}>
              <Select
                label={provinceDropdown.label}
                placeholder={provinceDropdown.hint}
                onChange={(val: string) => {
                  setProvince(val)
                  setHomeProvince(val)
                  setAnalyticsStart()
                }}
                feedback={!homeProvince && submitted ? provinceDropdown.customErrorMessage : undefined}
                validation={!homeProvince && submitted ? 'error' : undefined}
              >
                {provinceDropdown.options.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
        {renderOptions(homeLabel, getHomeTypes(province), setHomeType, homeType, homeError)}
        {renderOptions(energyLabel, getEnergyTypes(province), setEnergyType, energyType, energyError)}
        <Spacer space={5} />
        <Step1CtaContainer data-testid="newVersionStep1CTA">{renderCTAButton()}</Step1CtaContainer>
      </>
    )
  }

  return (
    <FlexGrid.Row ref={homeCardRef}>
      <FlexGrid.Col xs={12} lg={8}>
        <Card variant={{ padding: 'custom' }} tokens={{ borderWidth: 0 }}>
          <CardContainer>{renderHomeCardSelections()}</CardContainer>
        </Card>
      </FlexGrid.Col>
      <FlexGrid.Col xs={0} lg={4}>
        <HomeImage>
          <BlockRenderer block={homeImage} />
        </HomeImage>
      </FlexGrid.Col>
    </FlexGrid.Row>
  )
}

export default HomeCard
